/** Two-column layout component using CSS Grid with content projection. @component */

import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, input } from '@angular/core';
import type { CssClassInput, TwoColumnLayoutGap } from './types';

/**
 * Responsive Flexbox layout with configurable gap. Desktop: 50/50 columns; Mobile: single column stack. OnPush change
 * detection. ViewEncapsulation disabled for global style access.
 */
@Component({
  selector: 'hx-two-column-layout',
  templateUrl: 'two-column-layout.html',
  styleUrls: ['two-column-layout.css'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: {
    '[class]': 'hostClasses()',
    '[style.--custom-gap-value]': 'customGapValue()',
  },
})
export class HxTwoColumnLayout {
  /** Gap size between columns: small (16px), medium (24px), large (32px), or custom. Default: 'small'. */
  gap = input<TwoColumnLayoutGap>('small');

  /** Custom gap value (any CSS length unit) used when gap='custom'. */
  customGap = input<string | undefined>();

  /** Additional CSS classes: string, array, or undefined. Appended to base component classes. */
  cssClass = input<CssClassInput>();

  /** Computed CSS class list: base class + gap variant + custom classes. @internal */
  protected hostClasses = computed(() => {
    const classes: string[] = ['hx-two-column-layout', `hx-two-column-layout--gap-${this.gap()}`];

    const custom = this.cssClass();
    if (custom) {
      classes.push(...(Array.isArray(custom) ? custom : [custom]));
    }

    return classes.join(' ');
  });

  /**
   * Custom gap value for inline style binding.
   *
   * @remarks
   *   Returns customGap value only when gap='custom'. Used for CSS variable binding: `--custom-gap-value`
   * @internal
   */
  protected customGapValue = computed(() => {
    return this.gap() === 'custom' ? this.customGap() : undefined;
  });
}
